/**
 * Sidebar (normal + edit mode), built from the sidebar template (nav/SidebarFrame.tsx: frame, groups, menu buttons,
 * search, the edge toggle). Design: screenshots/17-navigation-system.png
 * Reorder: native drag-and-drop for pointers, and Space/Arrows/Esc for keyboard users.
 * Edit sidebar opens from the ⋯ next to any tab (shown on hover/focus).
 * `admin`: the admin dashboard's rail for Chicago Politics staff (navConfig ADMIN_TABS: Approvals, Organizations,
 * Analysis, Articles, Alerts, Traffic, each its own page) with none of the regular tabs, and a way back to the dashboard.
 * Both rails fold to their icons (the toggle in the header; navStore `collapsed`, the Shell passes it; wide screens only).
 * The header (RailHeader) holds the user menu (UserMenu: who you are, Settings, Members & roles, Sign out), the bell and
 * Search; the bottom of the regular rail is Admin for staff only. Edit sidebar opens from the ⋯ on any tab.
 */
import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  Activity, ArrowLeft, BarChart3, Bell, Bookmark, Building2, CalendarDays, Eye, EyeOff, GripVertical, Inbox, Landmark, LayoutGrid, Lock, LogOut, Megaphone,
  Mail, MoreHorizontal, Newspaper, PanelLeftClose, PanelLeftOpen, Pin, Plus, RotateCcw, ScrollText, Settings, Shield, ShieldCheck, Star, Tag, TrendingUp, UserRound, Users, MapPin,
} from "lucide-react";
import { useAnalytics } from "../analytics/analyticsStore";
import { navStore, useNav } from "./navStore";
import { ADMIN_TABS, GROUPS, adminTabOf, resolveSidebar, type CustomView, type OrgRole, type TabId, type UserGroup } from "./navConfig";
import { SidebarFrame, SidebarGroup, SidebarMenuButton, SidebarSearch, go } from "./SidebarFrame";
import { openNotifications, unreadCount, useNotifications } from "../notifications/notifications";
import { useFeaturesVersion } from "../realtime/entitlements";
import { useDismiss } from "../hooks/useDismiss";
import { initialsOf } from "../account/logic";

/** Who is signed in, for the user menu (Shell passes it from snapshot:me). */
export interface RailUser { name: string | null; email: string; /** On a team account: Team settings, Your account. */ team: boolean; /** Owner or admin: Members & roles. */ canManage: boolean }

const ICONS: Record<string, typeof LayoutGrid> = {
  "layout-grid": LayoutGrid, "calendar-days": CalendarDays, users: Users, "building-2": Building2,
  "scroll-text": ScrollText, "chart-column": BarChart3, "shield-check": ShieldCheck, settings: Settings, inbox: Inbox, megaphone: Megaphone,
  shield: Shield, activity: Activity, newspaper: Newspaper, mail: Mail, "map-pin": MapPin, "trending-up": TrendingUp,
};
const BADGES: Record<CustomView["badge"]["type"], typeof LayoutGrid> = { legislation: ScrollText, person: UserRound, topic: Tag, committee: Landmark };
const Icon = ({ name, size = 16 }: { name: string; size?: number }) => { const I = ICONS[name] ?? Bookmark; return <I size={size} />; };
/** The rail's tab icons, shared with the mobile bottom tab bar (screenshots/55). */
export const NavIcon = ({ name, size }: { name: string; size?: number }) => <span aria-hidden className="nav-icon"><Icon name={name} size={size} /></span>;

export function Sidebar({ group, role, staff = false, admin = false, collapsed = false, user, teamViews, activePath, newCounts, onNewView }: {
  group: UserGroup;
  role: OrgRole | null;
  /** Chicago Politics staff (snapshot:me `staff`): shows the Admin entry. */
  staff?: boolean;
  /** On the admin dashboard (/dashboard/admin/…, staff only): its own rail instead of the tabs. */
  admin?: boolean;
  /** Folded to its icons (navStore `collapsed`, never while editing; the Shell decides). */
  collapsed?: boolean;
  /** The signed-in person (the user menu); without it the header has no menu (tests). */
  user?: RailUser;
  teamViews: CustomView[];
  activePath: string;
  newCounts: Record<string, number>; // view id → new items (from the org channel)
  onNewView: (canPublishTeamViews: boolean) => void;
}) {
  const { prefs, editing } = useNav(useShallow((s) => ({ prefs: s.prefs, editing: s.editing })));
  // Starred dashboards under Analytics (screenshots/82, §0.7): titles from snapshot:me / the analytics store.
  const titles = useAnalytics((s) => s.titles);
  const features = useFeaturesVersion();   // the viewer's feature map changed (realtime/entitlements.ts)
  const sb = useMemo(() => (void features, resolveSidebar(group, role, prefs, teamViews, titles, staff)), [group, role, prefs, teamViews, titles, staff, features]);
  const order = sb.tabs.map((t) => t.id as TabId);
  const [lifted, setLifted] = useState<TabId | null>(null);
  const [dragging, setDragging] = useState<TabId | null>(null);
  const isActive = (path: string) => (path === "/dashboard" ? activePath === path : activePath.startsWith(path));

  const onKey = (e: KeyboardEvent, tab: TabId, i: number) => {
    if (tab === "overview") return;
    if (e.key === " ") { e.preventDefault(); setLifted(lifted === tab ? null : tab); }
    else if (lifted === tab && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      navStore.getState().move(tab, i + (e.key === "ArrowUp" ? -1 : 1), order);
    } else if (e.key === "Escape") setLifted(null);
  };

  // Rail Header (UX pass 1, §2.17): fold/unfold · the user menu · bell with unread dot · type-to-search (the ⌘K palette).
  const toggle = () => navStore.getState().setCollapsed(!collapsed);
  const logo = <RailHeader collapsed={collapsed} user={user} onToggle={toggle} />;

  if (admin) {
    const active = adminTabOf(activePath);
    return (
      <SidebarFrame label="Admin" admin collapsed={collapsed} onToggle={toggle} header={logo}
        footer={<SidebarMenuButton href="/dashboard" icon={<ArrowLeft size={16} />} label="Back to the dashboard" />}>
        <SidebarGroup label="Chicago Politics staff">
          {ADMIN_TABS.map((t) => <SidebarMenuButton key={t.id} href={t.path} icon={<Icon name={t.icon} />} label={t.label} active={active === t.id} />)}
        </SidebarGroup>
      </SidebarFrame>
    );
  }

  if (editing) {
    return (
      <SidebarFrame label="Dashboard" header={logo}>
          <section className="edit-card" aria-label="Edit sidebar">
            <header className="edit-head">
              <h2>Edit sidebar</h2>
              <button className="btn btn--primary btn--small" onClick={() => { setLifted(null); navStore.getState().setEditing(false); }}>Done</button>
            </header>
            <SectionLabel>Tabs</SectionLabel>
            <ol className="edit-list">
              {sb.tabs.map((t, i) => (
                <li
                  key={t.id}
                  className="edit-row"
                  data-lifted={lifted === t.id || undefined}
                  draggable={t.id !== "overview"}
                  onDragStart={() => setDragging(t.id as TabId)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragging) navStore.getState().move(dragging, i, order); setDragging(null); }}
                  onKeyDown={(e) => onKey(e, t.id as TabId, i)}
                  tabIndex={0}
                  aria-label={t.id === "overview" ? `${t.label}, pinned first` : `${t.label}. Space to move`}
                >
                  <span className="grip" aria-hidden>{t.id !== "overview" && <GripVertical size={12} />}</span>
                  <Icon name={t.icon} />
                  <span className="edit-label">{t.label}</span>
                  {t.id === "overview"
                    ? <span className="edit-icon" title="Overview stays first"><Pin size={14} /></span>
                    : <button className="edit-icon" aria-label={`Hide ${t.label}`} onClick={() => navStore.getState().toggleHidden(t.id as TabId)}><Eye size={14} /></button>}
                </li>
              ))}
              {sb.hidden.map((t) => (
                <li key={t.id} className="edit-row" data-hidden>
                  <span className="grip" aria-hidden><GripVertical size={12} /></span>
                  <Icon name={t.icon} />
                  <span className="edit-label">{t.label}</span>
                  <button className="edit-icon" aria-label={`Show ${t.label}`} onClick={() => navStore.getState().toggleHidden(t.id as TabId)}><EyeOff size={14} /></button>
                </li>
              ))}
            </ol>
            {sb.starredDashboards.length > 0 && <EditStarred items={sb.starredDashboards} />}
            {sb.personalViews.length > 0 && (
              <>
                <SectionLabel>Your views</SectionLabel>
                <ol className="edit-list">
                  {sb.personalViews.map((v) => {
                    const B = BADGES[v.badge.type];
                    return (
                      <li key={v.id} className="edit-row">
                        <span className="grip" aria-hidden><GripVertical size={12} /></span>
                        <span className="view-badge" data-type={v.badge.type}><B size={12} /></span>
                        <span className="edit-label">{v.label}</span>
                        <button className="edit-icon" aria-label={`Remove ${v.label}`} onClick={() => navStore.getState().removeView(v.id)}><MoreHorizontal size={14} /></button>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}
            <button className="nav-add-view" onClick={() => onNewView(sb.canPublishTeamViews)}><Plus size={14} />New view</button>
            {sb.unavailable.length > 0 && (
              <section aria-label="Not available">
                <SectionLabel>Not available for {GROUPS[group].label === "Resident" ? "residents" : GROUPS[group].label}</SectionLabel>
                {sb.unavailable.map(({ tab, reason }) => (
                  <div key={tab.id} className="edit-row" data-locked>
                    <Lock size={12} />
                    <Icon name={tab.icon} />
                    <span className="edit-label">{tab.label}<small>{reason}</small></span>
                  </div>
                ))}
              </section>
            )}
            <button className="edit-reset" onClick={() => navStore.getState().reset()}>
              <RotateCcw size={12} />Reset to {GROUPS[group].label} default
            </button>
          </section>
      </SidebarFrame>
    );
  }

  return (
    <SidebarFrame label="Dashboard" collapsed={collapsed} onToggle={toggle} header={logo}
      footer={sb.utilities.length > 0 ? sb.utilities.map((t) => <SidebarMenuButton key={t.id} href={t.path} icon={<Icon name={t.icon} />} label={t.label} active={isActive(t.path)} />) : undefined}>
      <SidebarGroup className="rail-tabs" aria-label="Tabs">
        {sb.tabs.map((t) => (
          <div key={t.id} className="nav-row">
            <SidebarMenuButton href={t.path} icon={<Icon name={t.icon} />} label={t.label} active={isActive(t.path)} />
            <button className="nav-more" aria-label="Edit sidebar" onClick={() => navStore.getState().setEditing(true)}><MoreHorizontal size={14} /></button>
          </div>
        ))}
      </SidebarGroup>
      {sb.starredDashboards.length > 0 && <StarredSection items={sb.starredDashboards} activePath={activePath} />}
      {sb.teamViews.length > 0 && <ViewSection title="Team views" views={sb.teamViews} newCounts={newCounts} />}
      <ViewSection title="Your views" views={sb.personalViews} newCounts={newCounts}>
        <button className="nav-add-view" onClick={() => onNewView(sb.canPublishTeamViews)}><Plus size={14} />New view</button>
      </ViewSection>
    </SidebarFrame>
  );
}

/**
 * Design component "Rail Header" (screenshots/17–18): the fold/unfold toggle (also ⌘B), then the user menu and the
 * bell with an unread dot, and the search field (type to search; no logo: removed 2026-10-14). Folded, the search is
 * its icon alone.
 */
function RailHeader({ collapsed, user, onToggle }: { collapsed: boolean; user?: RailUser; onToggle: () => void }) {
  const unread = useNotifications((s) => unreadCount(s.items));
  const Toggle = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <div className="rail__header">
      <div className="rail__header-top">
        <button className="icon-btn rail__fold" onClick={onToggle} aria-expanded={!collapsed} aria-keyshortcuts="Meta+B Control+B"
          aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"} title={collapsed ? "Expand the sidebar (⌘B)" : "Collapse the sidebar (⌘B)"}><Toggle size={16} /></button>
        <span className="rail__header-right">
          {user && <UserMenu user={user} collapsed={collapsed} />}
          <button className="icon-btn rail__bell" onClick={openNotifications} aria-haspopup="dialog"
            aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}><Bell size={16} />{unread > 0 && <span className="rail__dot" aria-hidden />}</button>
        </span>
      </div>
      <SidebarSearch collapsed={collapsed} />
    </div>
  );
}

/**
 * The user menu (left of the bell): your initials open who you are and where your settings live. Team accounts: Team
 * settings, Members & roles (owners and admins), Your account; individuals: Settings. Sign out ends the session
 * (Better Auth sign-out) and shows the signed-out dashboard.
 */
export function UserMenu({ user, collapsed = false }: { user: RailUser; collapsed?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useDismiss(ref, open, () => setOpen(false));
  const who = user.name || user.email;
  const item = (href: string, label: string, icon: ReactNode) => (
    <a key={href} role="menuitem" href={href} onClick={(e) => { setOpen(false); go(href)(e); }}>{icon}{label}</a>
  );
  const signOut = async () => {
    setOpen(false);
    await fetch("/api/auth/sign-out", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: "{}" }).catch(() => null);
    location.assign("/dashboard");
  };
  return (
    <span className="rail__user" ref={ref} data-collapsed={collapsed || undefined}>
      <button type="button" className="rail__avatar" aria-haspopup="menu" aria-expanded={open} aria-label={`Account menu, ${who}`} title={who} onClick={() => setOpen((v) => !v)}>
        {initialsOf(who) || "?"}
      </button>
      {open && (
        <span className="rail__user-pop" role="menu" aria-label="Account">
          <span className="rail__user-who"><b>{user.name || "Your account"}</b><small>{user.email}</small></span>
          {user.team ? <>
            {item("/dashboard/settings", "Team settings", <Settings size={14} aria-hidden />)}
            {user.canManage && item("/dashboard/settings/access", "Members & roles", <ShieldCheck size={14} aria-hidden />)}
            {item("/dashboard/settings/you", "Your account", <UserRound size={14} aria-hidden />)}
          </> : item("/dashboard/settings", "Settings", <Settings size={14} aria-hidden />)}
          <button type="button" role="menuitem" onClick={() => void signOut()}><LogOut size={14} aria-hidden />Sign out</button>
        </span>
      )}
    </span>
  );
}

const SectionLabel = ({ children }: { children: ReactNode }) => <h3 className="nav-section-label">{children}</h3>;

function ViewSection({ title, views, newCounts, children }: { title: string; views: CustomView[]; newCounts: Record<string, number>; children?: ReactNode }) {
  if (!views.length && !children) return null;
  return (
    <SidebarGroup className="rail-views" label={views.length > 0 ? title : undefined}>
      {views.map((v) => {
        const qs = new URLSearchParams(Object.entries(v.filters).flatMap(([k, val]) => (Array.isArray(val) ? val.map((x) => [k, x]) : [[k, val]])));
        const n = v.showCount ? newCounts[v.id] ?? 0 : 0;
        const B = BADGES[v.badge.type];
        const href = `/dashboard/${v.tab}?${qs}`;
        return (
          <a key={v.id} className="nav-item nav-view" href={href} onClick={go(href)}>
            <span className="view-badge" data-type={v.badge.type}><B size={12} /></span>
            <span className="nav-view-label">{v.label}</span>
            {n > 0 && <span className="new-count">{n}</span>}
          </a>
        );
      })}
      {children}
    </SidebarGroup>
  );
}

// ---------- Starred dashboards (Analytics, screenshots/82; brief §0.7) ----------

type Starred = { id: string; label: string; path: string };

/** "STARRED DASHBOARDS": each one opens the dashboard (aria-current on the open one); the star unstars it (Undo toast). */
function StarredSection({ items, activePath }: { items: Starred[]; activePath: string }) {
  return (
    <SidebarGroup className="rail-views rail__starred" aria-label="Starred dashboards" label="Starred dashboards">
      {items.map((d) => (
        <div key={d.id} className="nav-row nav-star-row">
          <a className="nav-item nav-view" href={d.path} aria-current={activePath === d.path ? "page" : undefined} onClick={go(d.path)}>
            <LayoutGrid size={16} aria-hidden />{d.label}
          </a>
          <button className="nav-star" aria-label={`Unstar ${d.label}`} title="Unstar · remove from sidebar"
            onClick={() => navStore.getState().toggleStar(d.id, d.label)}><Star size={14} aria-hidden /></button>
        </div>
      ))}
    </SidebarGroup>
  );
}

/** Edit sidebar: reorder starred dashboards like tabs (drag, or Space then arrows), or unstar them. */
function EditStarred({ items }: { items: Starred[] }) {
  const [lifted, setLifted] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const onKey = (e: KeyboardEvent, id: string, i: number) => {
    if (e.key === " ") { e.preventDefault(); setLifted(lifted === id ? null : id); }
    else if (lifted === id && (e.key === "ArrowUp" || e.key === "ArrowDown")) { e.preventDefault(); navStore.getState().moveStar(id, i + (e.key === "ArrowUp" ? -1 : 1)); }
    else if (e.key === "Escape") setLifted(null);
  };
  return (
    <>
      <SectionLabel>Starred dashboards</SectionLabel>
      <ol className="edit-list">
        {items.map((d, i) => (
          <li key={d.id} className="edit-row" data-lifted={lifted === d.id || undefined} draggable tabIndex={0} aria-label={`${d.label}. Space to move`}
            onDragStart={() => setDragging(d.id)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragging) navStore.getState().moveStar(dragging, i); setDragging(null); }} onKeyDown={(e) => onKey(e, d.id, i)}>
            <span className="grip" aria-hidden><GripVertical size={12} /></span>
            <LayoutGrid size={16} aria-hidden />
            <span className="edit-label">{d.label}</span>
            <button className="edit-icon nav-star" aria-label={`Unstar ${d.label}`} onClick={() => navStore.getState().toggleStar(d.id, d.label)}><Star size={14} /></button>
          </li>
        ))}
      </ol>
    </>
  );
}
